import throttle from "lodash/throttle";
import { type MutableRefObject, useEffect, useMemo, useRef } from "react";
import { devLog } from "utils/devtools/devLogging";

export interface ThrottleOptions {
  leading?: boolean;
  maxWait?: number;
  trailing?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TAnyFunc = (...args: any[]) => any;

function useLatest<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  // eslint-disable-next-line react-hooks/refs -- Intentionally keeping ref in sync without triggering re-renders
  ref.current = value;

  return ref;
}

const isNotFunction = (fn: TAnyFunc) => {
  if (typeof fn !== "function") {
    devLog({ level: "warn", message: `useThrottleFn expected parameter is a function, got ${typeof fn}` });
  }
};

const useUnmount = (fn: () => void) => {
  isNotFunction(fn);

  const fnRef = useLatest(fn);

  useEffect(
    () => () => {
      fnRef.current();
    },
    [fnRef],
  );
};

export function useThrottleFn<T extends TAnyFunc>(fn: T, wait = 1000, options?: ThrottleOptions) {
  isNotFunction(fn);

  const fnRef = useLatest(fn);

  const throttled = useMemo(
    () =>
      throttle(
        // eslint-disable-next-line react-hooks/refs -- Intentionally accessing latest function via ref
        (...args: Parameters<T>): ReturnType<T> => {
          return fnRef.current(...args);
        },
        wait,
        options,
      ),
    [fnRef, options, wait],
  );

  useUnmount(() => {
    throttled.cancel();
  });

  return throttled;
}
